import { Provider, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, skip } from 'rxjs/operators';
import { SpaConfig } from '@tourfold/libraries/spa-config/spa-config';
import { PlatformI18nService } from '@tourfold/shared-kernels/i18n';
import {
  REALTIME_AUTH_ADAPTER,
  REALTIME_CONFIG,
  RealtimeAuthAdapter,
  RealtimeConfig,
} from '@tourfold/shared-kernels/realtime';
import { AuthService } from '../../services/auth.service';
import { RefreshCoordinator } from '../../services/refresh-coordinator.service';

const SSE_ENDPOINT = '/frontend/v2/events/stream';
const HEARTBEAT_INTERVAL_MS = 90000;
const HEARTBEAT_GRACE_MS = 15000;
const RECONNECT_MAX_ATTEMPTS = 5;
const RECONNECT_BASE_DELAY = 1000;
const RECONNECT_MAX_DELAY = 90000;

/**
 * Wires the realtime kernel for the web-app shell.
 *
 *  - `REALTIME_CONFIG` builds the V2 SSE stream URL from `SpaConfig`.
 *  - `REALTIME_AUTH_ADAPTER` delegates to web-app's `AuthService`,
 *    `RefreshCoordinator`, and `PlatformI18nService`. `tokenChanges$`
 *    bridges the `AuthService.accessToken` signal to an Observable and
 *    skips the bootstrap value so the kernel only reconnects on real
 *    rotations.
 */
export const WEB_REALTIME_PROVIDERS: Provider[] = [
  {
    provide: REALTIME_CONFIG,
    useValue: {
      buildStreamUrl: (lastEventId: string | null): string => {
        const rootUrl = SpaConfig.apiUrlProvider.rootUrl ?? window.location.origin;
        if (!lastEventId) {
          return `${rootUrl}${SSE_ENDPOINT}`;
        }
        return `${rootUrl}${SSE_ENDPOINT}?last_event_id=${encodeURIComponent(lastEventId)}`;
      },
      heartbeatTimeoutMs: HEARTBEAT_INTERVAL_MS + HEARTBEAT_GRACE_MS,
      reconnectMaxAttempts: RECONNECT_MAX_ATTEMPTS,
      reconnectBaseDelayMs: RECONNECT_BASE_DELAY,
      reconnectMaxDelayMs: RECONNECT_MAX_DELAY,
    } satisfies RealtimeConfig,
  },
  {
    provide: REALTIME_AUTH_ADAPTER,
    useFactory: (): RealtimeAuthAdapter => {
      const authService = inject(AuthService);
      const refreshCoordinator = inject(RefreshCoordinator);
      const platformI18n = inject(PlatformI18nService);

      const tokenChanges$: Observable<string> = toObservable(authService.accessToken).pipe(
        distinctUntilChanged(),
        skip(1),
        filter((token): token is string => !!token && token.length > 0)
      );

      return {
        getAccessToken: () => authService.getAccessToken(),
        getTenantId: () => authService.getTenantId() ?? null,
        coordinateRefresh: () => refreshCoordinator.coordinateRefresh(),
        resolveAcceptLanguage: () => platformI18n.resolveAcceptLanguage(),
        tokenChanges$,
      };
    },
  },
];
